import { Title, UnstyledButton } from '@mantine/core'
import { InfoWindow, Map, Marker } from '@vis.gl/react-google-maps'
import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import GemIcon from '../../assets/GemIcon.png'
import { Location } from '../../models/Location'
import LocationInfoWindow from './LocationInfoWindow'

interface LocationsMapProps {
  locations: Location[]
  center?: { lat: number; lng: number } | null
  zoom?: number
  onViewChange?: (
    center: { lat: number; lng: number },
    zoom: number,
    bounds?: {
      north: number
      south: number
      east: number
      west: number
    } | null,
  ) => void
}

const LocationsMap = ({
  locations,
  center,
  zoom,
  onViewChange,
}: LocationsMapProps) => {
  const navigate = useNavigate()
  const [clickedLocation, setClickedLocation] = useState<Location | null>(null)
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  // Filter locations that have coordinates
  const locationsWithCoordinates = locations.filter(
    (location) => location.latitude && location.longitude,
  )

  // Calculate center point from locations if not provided
  const defaultCenter =
    locationsWithCoordinates.length > 0
      ? {
          lat:
            locationsWithCoordinates.reduce(
              (sum, loc) => sum + loc.latitude!,
              0,
            ) / locationsWithCoordinates.length,
          lng:
            locationsWithCoordinates.reduce(
              (sum, loc) => sum + loc.longitude!,
              0,
            ) / locationsWithCoordinates.length,
        }
      : { lat: 35.9653, lng: -83.9233 } // Default to San Francisco

  const mapCenter = center || defaultCenter
  const mapZoom = zoom || 12

  const handleMarkerClick = (location: Location) => {
    setClickedLocation(location)
  }

  const handleInfoWindowClose = () => {
    setClickedLocation(null)
  }

  const handleMapClick = () => {
    setClickedLocation(null)
  }

  const handleMapIdle = (event: any) => {
    if (onViewChange && event.map) {
      const map = event.map
      const center = map.getCenter()
      const zoom = map.getZoom()
      const bounds = map.getBounds()
      if (center && zoom !== undefined && bounds) {
        const boundsData = {
          north: bounds.getNorthEast().lat(),
          south: bounds.getSouthWest().lat(),
          east: bounds.getNorthEast().lng(),
          west: bounds.getSouthWest().lng(),
        }

        // Clear any existing timeout
        if (debounceTimeoutRef.current) {
          clearTimeout(debounceTimeoutRef.current)
        }

        // Debounce the search to prevent excessive API calls
        debounceTimeoutRef.current = setTimeout(() => {
          onViewChange(
            { lat: center.lat(), lng: center.lng() },
            zoom,
            boundsData,
          )
        }, 1000) // 1000ms delay
      }
    }
  }

  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current)
      }
    }
  }, [])

  return (
    <div style={{ height: '100%', width: '100%' }}>
      <Map
        mapTypeControl={false}
        mapId='locations-map'
        style={{ height: '100%', width: '100%' }}
        defaultCenter={mapCenter}
        defaultZoom={mapZoom}
        minZoom={10}
        zoomControl={true}
        onClick={handleMapClick}
        streetViewControl={false}
        onIdle={handleMapIdle}
      >
        {locationsWithCoordinates.map((location) => (
          <Marker
            key={location.id}
            position={{
              lat: location.latitude!,
              lng: location.longitude!,
            }}
            clickable={true}
            onClick={() => handleMarkerClick(location)}
            title={location.name}
            icon={{
              url: GemIcon,
              scaledSize: new google.maps.Size(40, 40),
              anchor: new google.maps.Point(20, 35),
            }}
          />
        ))}

        {clickedLocation && (
          <InfoWindow
            position={{
              lat: clickedLocation.latitude!,
              lng: clickedLocation.longitude!,
            }}
            pixelOffset={[0, -40]}
            onCloseClick={handleInfoWindowClose}
            headerContent={
              <UnstyledButton
                onClick={() => {
                  navigate(`/locations/${clickedLocation.id}`)
                }}
              >
                <Title
                  size='h3'
                  title={clickedLocation.name}
                  style={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    marginTop: -8,
                  }}
                >
                  {clickedLocation.name}
                </Title>
              </UnstyledButton>
            }
          >
            <div style={{ maxWidth: '300px', padding: '8px' }}>
              <LocationInfoWindow location={clickedLocation} />
            </div>
          </InfoWindow>
        )}
      </Map>
    </div>
  )
}

export default LocationsMap
